import { motion } from 'framer-motion'
import { Instagram, Facebook } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'

const footerColumns = [
  {
    title: 'Servizio Clienti',
    links: [
      { label: 'Contattaci', href: '/it/contattaci', routeKey: 'contacts' },
      { label: 'FAQ', href: '/it/faq', routeKey: 'faq' },
      { label: 'Privacy Policy', href: '/it/privacy-policy', routeKey: 'privacy' },
      { label: 'Condizioni Generali di Vendita – Welcome Box', href: '/it/condizioni-generali-di-vendita-welcome-box', routeKey: 'condizioniWelcomeBox' },
      { label: 'Sitemap', href: '/it/sitemap', routeKey: 'sitemap' },
    ],
  },
  {
    title: 'Chi Siamo',
    links: [
      { label: 'Il Mondo Nashi', href: '/it/il-mondo-nashi-argan', routeKey: 'world' },
      { label: 'Store Locator', href: '/it/store-locator', routeKey: 'storeLocator' },
      { label: 'Regali Aziendali', href: '/it/regali-aziendali', routeKey: 'regaliAziendali' },
    ],
  },
  {
    title: 'Programma Fedeltà',
    links: [
      { label: 'NashiLovers Club', href: '/it/nashilovers-club', routeKey: 'nashiLoversClub' },
      { label: 'Regolamento', href: '/it/regolamento', routeKey: 'regolamento' },
      { label: 'e-Gift Card', href: '/it/e-gift-card', routeKey: 'eGiftCard' },
    ],
  },
  {
    title: 'Unisciti alla famiglia Nashi',
    links: [
      { label: 'Diventa Partner', href: '/it/diventa-partner', routeKey: 'diventaPartner' },
      { label: 'Lavora con noi', href: '/it/lavora-con-noi', routeKey: 'lavoraConNoi' },
    ],
  },
]

export function Footer() {
  return (
    <footer className="relative bg-bruno text-cream overflow-hidden" aria-label="Footer">
      {/* Decorative top divider */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-argan/40 to-transparent" />

      {/* Massive editorial wordmark */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-16 left-1/2 -translate-x-1/2 select-none font-display italic text-cream/[0.04] whitespace-nowrap"
        style={{
          fontSize: 'clamp(8rem, 22vw, 22rem)',
          lineHeight: 1,
          letterSpacing: '-0.04em',
        }}
      >
        nashi argan
      </div>

      <div className="relative z-10 nashi-container pt-20 md:pt-28 pb-8 md:pb-10">
        {/* Top — Brand + Etichettatura ambientale */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20 md:mb-28">
          <div className="lg:col-span-5">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.1 }}
              className="font-display italic text-cream/90 text-3xl md:text-4xl"
            >
              nashi <span className="not-italic font-light tracking-[0.4em] text-argan">argan</span>
            </motion.span>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.25 }}
              className="mt-6 max-w-md text-cream/70 text-base leading-relaxed"
            >
              Il Brand cosmetico 100% made in Italy che ascolta e risponde alle
              esigenze con passione ed efficacia.
            </motion.p>

            <div className="mt-10 flex items-center gap-5 text-cream/50">
              <a
                href="https://www.instagram.com/nashiargan/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-argan-soft transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-4 w-4" strokeWidth={1.5} />
              </a>
              <a
                href="https://www.facebook.com/NashiArganOfficial"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-argan-soft transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-4 w-4" strokeWidth={1.5} />
              </a>
              <span className="block w-px h-4 bg-cream/20" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-cream/40">
                @nashiargan
              </span>
            </div>
          </div>

          {/* Right — Etichettatura ambientale */}
          <div className="lg:col-span-7 lg:pl-12 lg:border-l lg:border-cream/10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              <p className="text-[10px] uppercase tracking-[0.32em] text-argan-soft mb-4">
                Etichettatura ambientale
              </p>
              <p className="font-display italic text-cream text-2xl md:text-3xl leading-tight mb-4">
                Smaltisci con cura.
              </p>
              <p className="text-sm text-cream/70 leading-relaxed max-w-md">
                Scopri come smaltire e riciclare la confezione dopo aver
                utilizzato il prodotto.
              </p>
              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.18em]">
                <a
                  href="https://packaging.nashiargan.it/it/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cream/70 hover:text-cream border-b border-cream/30 hover:border-cream pb-1 transition-colors"
                >
                  Nashi Argan →
                </a>
                <a
                  href="https://pro.packaging.nashiargan.it/it/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cream/70 hover:text-cream border-b border-cream/30 hover:border-cream pb-1 transition-colors"
                >
                  Nashi Pro →
                </a>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 lg:gap-12 pb-16 border-b border-cream/10">
          {footerColumns.map((column, idx) => (
            <motion.div
              key={column.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.1 + idx * 0.1 }}
            >
              <p className="font-display italic text-argan-soft text-base mb-6">
                — {column.title}
              </p>
              <ul className="space-y-3">
                {column.links.map((link) => (
                  <li key={link.label}>
                    {'routeKey' in link && link.routeKey ? (
                      <LocalizedLink
                        to={link.routeKey}
                        className="text-sm text-cream/70 hover:text-cream hover:tracking-wide transition-all duration-500"
                      >
                        {link.label}
                      </LocalizedLink>
                    ) : (
                      <a
                        href={link.href}
                        className="text-sm text-cream/70 hover:text-cream hover:tracking-wide transition-all duration-500"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Bottom — legal */}
        <div className="mt-10 flex flex-col gap-6 text-[11px] tracking-wide text-cream/50">
          <p className="leading-relaxed">
            Nashi Argan s.r.l. REA: Milano | N. 2041243 Registro Imp. di MI,
            C.F. e P.IVA 01594370197 | © Landoll s.r.l. 2014–{new Date().getFullYear()}.
            All rights reserved
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <LocalizedLink to="privacy" className="hover:text-cream transition-colors">
              Informativa sulla privacy
            </LocalizedLink>
            <LocalizedLink to="cookiePolicy" className="hover:text-cream transition-colors">
              Cookie Policy
            </LocalizedLink>
            <LocalizedLink to="sitemap" className="hover:text-cream transition-colors">
              Sitemap
            </LocalizedLink>
          </div>
        </div>
      </div>
    </footer>
  )
}
